import { trendingNow, formatSocialCount, formatReachCount, type ViralEntry, type EntityType } from '@/mocks/rfwSocial';
import { Link } from 'react-router-dom';

const typeLabels: Record<string, { label: string; icon: string; color: string }> = {
  radio:   { label: 'Radio',     icon: 'ri-radio-line',           color: 'bg-sky-100 text-sky-700 border-sky-200' },
  program: { label: 'Programa',  icon: 'ri-calendar-check-line',  color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  host:    { label: 'Locutor',   icon: 'ri-mic-line',             color: 'bg-amber-100 text-amber-700 border-amber-200' },
  event:   { label: 'Evento',    icon: 'ri-calendar-event-line',  color: 'bg-violet-100 text-violet-700 border-violet-200' },
  contest: { label: 'Concurso',  icon: 'ri-gift-line',            color: 'bg-red-100 text-red-700 border-red-200' },
};

export default function SocialSection() {
  return (
    <section className="py-14 md:py-20 bg-background-50">
      <div className="max-w-[1280px] mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-red-100 border border-red-200 mb-5">
            <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></span>
            <span className="text-[11px] font-bold text-red-700 uppercase tracking-wider whitespace-nowrap">Social Total</span>
          </div>
          <h2 className="text-2xl md:text-4xl font-extrabold text-foreground-950 mb-3">
            Lo que todos están compartiendo
          </h2>
          <p className="text-sm md:text-base text-foreground-500 max-w-xl mx-auto leading-relaxed">
            Radios, programas, locutores, eventos y concursos que están marcando tendencia ahora mismo.
          </p>
        </div>

        {/* Trending Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {trendingNow.slice(0, 8).map((item) => (
            <ViralCard key={`${item.entityType}-${item.entityId}`} item={item} />
          ))}
        </div>

        {/* Stats Footer */}
        <div className="mt-10 bg-background-100 rounded-3xl p-6 md:p-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { icon: 'ri-share-forward-line', color: 'bg-sky-100 text-sky-600', label: 'Compartidos Hoy', value: '287K' },
              { icon: 'ri-eye-line', color: 'bg-violet-100 text-violet-600', label: 'Alcance Total', value: '6.8M' },
              { icon: 'ri-flashlight-line', color: 'bg-amber-100 text-amber-600', label: 'Interacciones', value: '1.2M' },
              { icon: 'ri-rocket-line', color: 'bg-emerald-100 text-emerald-600', label: 'En Tendencia', value: '42 activos' },
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <div className={`w-12 h-12 rounded-2xl ${stat.color} flex items-center justify-center mx-auto mb-3`}>
                  <i className={`${stat.icon} text-lg`}></i>
                </div>
                <p className="text-xl md:text-2xl font-extrabold text-foreground-950">{stat.value}</p>
                <p className="text-[11px] font-semibold text-foreground-400 uppercase tracking-wider mt-0.5">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-8">
          <Link
            to="/rankings"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 dark:text-foreground-950 rounded-xl text-sm font-bold hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
          >
            Ver Rankings Completos
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}

function ViralCard({ item }: { item: ViralEntry }) {
  const typeInfo = typeLabels[item.entityType] || typeLabels.radio;
  return (
    <Link
      to={item.entityLink}
      className="group bg-background-50 rounded-2xl border border-background-200/70 hover:border-background-300/60 hover:shadow-sm transition-all overflow-hidden block"
    >
      {/* Image */}
      <div className="relative w-full h-40 overflow-hidden">
        <img
          src={item.entityImageUrl}
          alt={item.entityName}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>

        {/* Trend badge */}
        <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500 text-white text-[9px] font-bold">
          <i className="ri-arrow-up-line text-[8px]"></i>
          +{item.trendPercent}%
        </div>

        {/* Ranking badge */}
        <div className="absolute top-3 left-3 w-7 h-7 rounded-lg bg-background-50/90 backdrop-blur-sm flex items-center justify-center text-xs font-extrabold text-foreground-950">
          #{item.ranking}
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-semibold border ${typeInfo.color}`}>
          <i className={`${typeInfo.icon} text-[8px]`}></i>
          {typeInfo.label}
        </span>
        <h4 className="text-sm font-bold text-foreground-950 mt-2 line-clamp-1 group-hover:text-primary-600 transition-colors">
          {item.entityName}
        </h4>
        <p className="text-[11px] text-foreground-400 mt-0.5 line-clamp-1">{item.entitySubtitle}</p>

        {/* Mini stats */}
        <div className="flex items-center gap-3 mt-3 pt-3 border-t border-background-200/70 text-[10px] text-foreground-500">
          <span className="flex items-center gap-1">
            <i className="ri-share-forward-line text-[9px]"></i>
            {formatSocialCount(item.shares)}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-eye-line text-[9px]"></i>
            {formatReachCount(item.reach)}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-flashlight-line text-[9px]"></i>
            {formatSocialCount(item.interactions)}
          </span>
        </div>
      </div>
    </Link>
  );
}